import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import { Tag } from '../types/authTypes';
import { useLanguage } from '../i18n/LanguageContext';
import './TagManagementModal.css';

interface TagManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTagsUpdated: () => void;
  asFullView?: boolean; // Optional prop to render as full view instead of modal
}

interface TagWithCounts extends Tag {
  entry_count: number;
}

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

interface PromptModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  defaultValue: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText,
  cancelText
}) => {
  // The hook can't run in a parameter default, so the fallbacks resolve here
  const { t } = useLanguage();
  const confirmLabel = confirmText ?? t.common.confirm;
  const cancelLabel = cancelText ?? t.common.cancel;

  if (!isOpen) return null;

  return (
    <div className="tag-management-confirm-modal-overlay">
      <div className="tag-management-confirm-modal">
        <h3>{title}</h3>
        <div className="tag-management-confirm-message">
          {message.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
        <div className="tag-management-confirm-actions">
          <button 
            onClick={onConfirm} 
            className="tag-management-confirm-btn"
          >
            {confirmLabel}
          </button>
          <button onClick={onCancel} className="tag-management-cancel-btn">
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  title,
  message,
  defaultValue,
  onConfirm,
  onCancel
}) => {
  const { t } = useLanguage();
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (value.trim()) {
      onConfirm(value.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="tag-management-confirm-modal-overlay">
      <div className="tag-management-confirm-modal">
        <h3>{title}</h3>
        <div className="tag-management-confirm-message">{message}</div>
        <div>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t.tagManagement.enterTagName}
            autoFocus
            className="tag-management-prompt-input"
          />
          <div className="tag-management-confirm-actions">
            <button 
              type="button" 
              onClick={handleSubmit} 
              className="tag-management-confirm-btn" 
              disabled={!value.trim()}
            >
              {t.common.confirm}
            </button>
            <button type="button" onClick={onCancel} className="tag-management-cancel-btn">
              {t.common.cancel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TagManagementModal: React.FC<TagManagementModalProps> = ({ isOpen, onClose, onTagsUpdated, asFullView = false }) => {
  const { t } = useLanguage();
  const [tags, setTags] = useState<TagWithCounts[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [editingTag, setEditingTag] = useState<TagWithCounts | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('');
  const [newTextColor, setNewTextColor] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'count' | 'color'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showMergePrompt, setShowMergePrompt] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mergeTargetName, setMergeTargetName] = useState('');
  const [pendingMergeData, setPendingMergeData] = useState<{
    selectedTagObjects: TagWithCounts[];
    targetTag: TagWithCounts;
    mergeName: string;
  } | null>(null);

  const colorPairs: { [key: string]: string } = {
    "#5b94f1ff": "#222222",
    "#ea6e6eff": "#444444", 
    "#5becbbff": "#15524a",
    "#efbc65ff": "#4a3510",
    "#b497f6ff": "#2e1d5c",
    "#7de9f9ff": "#08404a",
    "#f4bdd9ff": "#5a2440",
    "#f99a7aff": "#5a2918",
    "#f7e499ff": "#4f3e05",
    "#f797f1ff": "#5a0e4e",
    "#8160cfff": "#dddddd",
    "#06b6d4ff": "#0a3d47",
    "#10b981ff": "#0d4a2b",
    "#3b82f6ff": "#1e3a5f",
    "#45b7d1ff": "#1a4b5c",
    "#4ecdc4ff": "#1a4944",
    "#84cc16ff": "#2d4a08",
    "#8b5cf6ff": "#2d1b5c",
    "#96ceb4ff": "#2d4a3b",
    "#98d8c8ff": "#2a4a42",
    "#98d8e8ff": "#2a4a52",
    "#dda0ddff": "#5c2d5c",
    "#ec4899ff": "#5c1d3b",
    "#f59e0bff": "#5c3a08",
    "#f7dc6fff": "#5c4f1a",
    "#ff6b6bff": "#5c1f1f",
    "#ffeaa7ff": "#5c4d1a"
  };

  const colors = Object.keys(colorPairs);
  const textColors = Object.keys(colorPairs).map(key => colorPairs[key]);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/tags/');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const filteredTags = tags
    .filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'count':
          aValue = a.entry_count;
          bValue = b.entry_count;
          break;
        case 'color':
          aValue = a.color;
          bValue = b.color;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const handleTagSelect = (tagId: number) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTags(newSelected);
  };

  const handleEditTag = (tag: TagWithCounts) => {
    setEditingTag(tag);
    setNewName(tag.name);
    setNewColor(tag.color);
    setNewTextColor(tag.text_color || '#ffffff');
  };

  const handleSaveEdit = async () => {
    if (!editingTag || !newName.trim()) return;

    try {
      setIsLoading(true);
      await api.patch(`/tags/${editingTag.id}/`, {
        name: newName.toLowerCase().trim().replace(/\s+/g, '-'),
        color: newColor,
        text_color: newTextColor
      });

      setEditingTag(null);
      setNewName('');
      setNewColor('');
      setNewTextColor('');
      await fetchTags();
      onTagsUpdated();
    } catch (error) {
      console.error('Failed to update tag:', error);
      alert(t.tagManagement.failedToUpdate);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMergeTags = async () => {
    if (selectedTags.size < 2) {
      alert(t.tagManagement.selectAtLeast2);
      return;
    }

    const selectedTagObjects = tags.filter(tag => selectedTags.has(tag.id));
    
    const targetTag = selectedTagObjects.reduce((prev, current) => 
      prev.entry_count > current.entry_count ? prev : current
    );

    setMergeTargetName(targetTag.name);
    setPendingMergeData({ selectedTagObjects, targetTag, mergeName: '' });
    setShowMergePrompt(true);
  };

  const handleMergePromptConfirm = (mergeName: string) => {
    if (pendingMergeData) {
      const updatedData = {
        ...pendingMergeData,
        mergeName
      };
      setPendingMergeData(updatedData);
      setShowMergePrompt(false);
      setShowMergeConfirm(true);
    }
  };

  const executeMerge = async () => {
    if (!pendingMergeData) return;

    try {
      setIsLoading(true);
      
      const selectedTagIds = Array.from(selectedTags);
      const processedTargetName = pendingMergeData.mergeName.toLowerCase().trim().replace(/\s+/g, '-');
      
      const requestData = {
        tag_ids: selectedTagIds,
        target_name: processedTargetName,
        target_color: pendingMergeData.targetTag.color
      };
      
      const response = await api.post('/tags/merge/', requestData);

      if (response.status === 200) {
        setSelectedTags(new Set());
        await fetchTags();
        onTagsUpdated();
      }
    } catch (error) {
      console.error('Error merging tags:', error);
      alert(t.tagManagement.failedToMerge);
    } finally {
      setIsLoading(false);
      setShowMergeConfirm(false);
      setPendingMergeData(null);
    }
  };

  const handleDeleteTags = async () => {
    if (selectedTags.size === 0) {
      alert(t.tagManagement.selectTagsToDelete);
      return;
    }

    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    try {
      setIsLoading(true);
      
      await api.post('/tags/bulk_delete/', {
        tag_ids: Array.from(selectedTags)
      });

      setSelectedTags(new Set());
      await fetchTags();
      onTagsUpdated();
    } catch (error) {
      console.error('Failed to delete tags:', error);
      alert(t.tagManagement.failedToDelete);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const getSelectedTagObjects = () => tags.filter(tag => selectedTags.has(tag.id));

  if (!isOpen) return null;

  const selectedTagObjects = getSelectedTagObjects();

  return (
    <>
      <div className="tag-management-modal-overlay" onClick={asFullView ? undefined : onClose}>
        <div className="tag-management-modal" onClick={e => e.stopPropagation()}>
          <div className="tag-management-modal-header">
            <h2>{t.tagManagement.title}</h2>
            {!asFullView && (
              <button className="tag-management-close-btn" aria-label={t.common.close} onClick={onClose}>×</button>
            )}
          </div>

          <div className="tag-management-modal-body">
            <div className="tag-management-controls-section">
              <div className="tag-management-search-sort-controls">
                <input
                  type="text"
                  placeholder={t.tagManagement.searchTags}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="tag-management-search-input"
                />
                
                <select 
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [sort, order] = e.target.value.split('-');
                    setSortBy(sort as 'name' | 'count' | 'color');
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="tag-management-sort-select"
                >
                  <option value="name-asc">{t.tagManagement.nameAZ}</option>
                  <option value="name-desc">{t.tagManagement.nameZA}</option>
                  <option value="count-desc">{t.tagManagement.mostUsed}</option>
                  <option value="count-asc">{t.tagManagement.leastUsed}</option>
                  <option value="color-asc">{t.tagManagement.color}</option>
                </select>
              </div>

              <div className="tag-management-bulk-actions">
                <span className="tag-management-selection-count">
                  {selectedTags.size} {t.tagManagement.selected}
                </span>
                <button
                  onClick={handleMergeTags}
                  disabled={selectedTags.size < 2 || isLoading}
                  className="tag-management-merge-btn"
                >
                  {t.tagManagement.mergeSelected}
                </button>
                <button
                  onClick={handleDeleteTags}
                  disabled={selectedTags.size === 0 || isLoading}
                  className="tag-management-delete-btn"
                >
                  {t.tagManagement.deleteSelected}
                </button>
              </div>
            </div>

            <div className="tag-management-tags-list">
              {isLoading ? (
                <div className="tag-management-loading">{t.tagManagement.loadingTags}</div>
              ) : (
                filteredTags.map((tag, index) => {
                  return (
                    <div 
                      key={tag.id} 
                      className={`tag-management-tag-item ${selectedTags.has(tag.id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.has(tag.id)}
                        onChange={() => handleTagSelect(tag.id)}
                        className="tag-management-tag-checkbox"
                      />
                      
                      <div 
                        className="tag-management-tag-display"
                        style={{ 
                          backgroundColor: tag.color,
                          color: tag.text_color || '#ffffff'
                        }}
                      >
                        {tag.name}
                      </div>
                      
                      <div className="tag-management-tag-info">
                        <span className="tag-management-entry-count">{tag.entry_count} {t.tagManagement.entries}</span>
                        <button
                          onClick={() => handleEditTag(tag)}
                          className="tag-management-edit-tag-btn"
                          disabled={isLoading}
                        >
                          {t.tagManagement.edit}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="tag-management-stats-section">
              <div className="tag-management-stats">
                <span>{t.tagManagement.totalTags}: {tags.length}</span>
                <span>{t.tagManagement.showing}: {filteredTags.length}</span>
                <span>{t.tagManagement.totalEntries}: {tags.reduce((sum, tag) => sum + tag.entry_count, 0)}</span>
              </div>
            </div>
          </div>

          {editingTag && (
            <div className="tag-management-edit-modal">
              <div className="tag-management-edit-content">
                <h3>{t.tagManagement.editTag}: {editingTag.name}</h3>
                
                <div className="tag-management-edit-field">
                  <label>{t.tagManagement.name}:</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t.tagManagement.tagName}
                  />
                </div>

                <div className="tag-management-edit-field">
                  <label>{t.tagManagement.color}:</label>
                  <div className="tag-management-color-picker">
                    {colors.map(color => (
                      <div
                        key={color}
                        className={`tag-management-color-option ${newColor === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewColor(color)}
                      />
                    ))}
                  </div>
                </div>

                <div className="tag-management-edit-field">
                  <label>{t.tagManagement.textColor}:</label>
                  <div className="tag-management-color-picker">
                    {textColors.map(color => (
                      <div
                        key={color}
                        className={`tag-management-color-option ${newTextColor === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color, border: color === '#ffffff' || color === '#f0f0f0' || color === '#dddddd' ? '2px solid #666' : 'none' }}
                        onClick={() => setNewTextColor(color)}
                      />
                    ))}
                  </div>
                </div>

                <div className="tag-management-edit-actions">
                  <div className="tag-management-preview">
                    <span 
                      className="tag-management-preview-tag"
                      style={{ 
                        backgroundColor: newColor,
                        color: newTextColor
                      }}
                    >
                      {newName || t.tagManagement.preview}
                    </span>
                  </div>
                  <div className="tag-management-edit-buttons">
                    <button onClick={handleSaveEdit} disabled={!newName.trim() || isLoading}>
                      {t.tagManagement.saveChanges}
                    </button>
                    <button onClick={() => setEditingTag(null)}>
                      {t.tagManagement.cancel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Merge Prompt Modal */}
      <PromptModal
        isOpen={showMergePrompt}
        title={t.tagManagement.mergeTags}
        message={`${t.tagManagement.mergeInto}:`}
        defaultValue={mergeTargetName}
        onConfirm={handleMergePromptConfirm}
        onCancel={() => {
          setShowMergePrompt(false);
          setPendingMergeData(null);
        }}
      />

      {/* Merge Confirmation Modal */}
      <ConfirmModal
        isOpen={showMergeConfirm}
        title={t.tagManagement.confirmMerge}
        message={pendingMergeData ? 
          `${t.tagManagement.confirmMergeMessage} "${pendingMergeData.mergeName}":\n\n` +
          pendingMergeData.selectedTagObjects.map(tag => `• ${tag.name} (${tag.entry_count} ${t.tagManagement.entries})`).join('\n') +
          `\n\n${t.tagManagement.cannotUndo}` : ''
        }
        onConfirm={executeMerge}
        onCancel={() => {
          setShowMergeConfirm(false);
          setPendingMergeData(null);
        }}
        confirmText={t.tagManagement.mergeTags}
        cancelText={t.tagManagement.cancel}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title={t.tagManagement.confirmDelete}
        message={selectedTagObjects.length > 0 ?
          `${t.tagManagement.confirmDeleteMessage.replace('{count}', selectedTagObjects.length.toString())}\n\n` +
          selectedTagObjects.map(tag => `• ${tag.name} (${tag.entry_count} ${t.tagManagement.entries})`).join('\n') +
          `\n\n${t.tagManagement.willRemoveTags.replace('{count}', selectedTagObjects.reduce((sum, tag) => sum + tag.entry_count, 0).toString())}\n${t.tagManagement.cannotUndo}` : ''
        }
        onConfirm={executeDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText={t.tagManagement.deleteTags}
        cancelText={t.tagManagement.cancel}
      />
    </>
  );
};

export default TagManagementModal;